import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ClipboardEvent, DragEvent } from 'react';
import { createPortal } from 'react-dom';
// 메뉴 자리 잡기는 첨부 저장 메뉴와 같은 규칙을 쓴다 — 입력창은 화면 맨 아래라
// 아래로 열면 창 밖으로 나간다(실제로 그렇게 깨졌다).
import { useAnchoredMenu } from '../../../hooks/useAnchoredMenu';
import { useChatStore } from '../../../store/chatStore';
import { sendMessage } from '../../../api/chat';
import { useAttachmentUpload, AttachmentUploader, WorkspaceFilePicker } from '../../../components/Attachment';
import { useAuthStore } from '../../../store/authStore';
import { toMessageAttachment } from '../../../lib/attachments';
import { AtSign, Bot, Mic, Paperclip, Send, Smile, X } from '../../../icons';
import { EmojiPicker } from './EmojiPicker';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';
import { useEnterToSend } from '../../../hooks/useEnterToSend';
// 붙여넣기·드래그에서 파일을 꺼내는 규칙 — AI 컴포저와 **같은 것**을 쓴다.
import { filesFromTransfer, transferHasFiles } from '../../../lib/transfer-files';

interface MessageInputProps {
  roomId: string;
}

/**
 * MessageInput (P2-16i + P2-17) — 시안 line 1211~1246.
 *
 * 책임:
 *   - room 별 inputValue 보존 (chatStore — room 전환 시에도 유지)
 *   - Enter (no shift) 로 send. Shift+Enter 로 줄바꿈 (textarea)
 *   - send 시 Idempotency-Key (UUID v4) + content trim. backend 412 / 200 reused 응답은 silent.
 *   - **첨부 (P2-17)**: Paperclip 버튼 → file picker → presigned PUT → MinIO 직접 업로드 →
 *     attachments 배열 누적. send 시 message.attachments 에 결합. 단일 메시지에 여러 첨부 가능.
 *
 * 시안 toolbar 의 서식 버튼(Bold/Italic/List/Code/Link)은 미사용으로 제거(2026-06-30 사용자 요청).
 * @AI mention popover 는 후속 atomic.
 */
// 전송 실패(특히 429/503)를 사용자 친화 메시지로 변환. 서버 code 별 분기.
function friendlySendError(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { code?: string; retryAfterSec?: number; message?: string } } };
  const status = e?.response?.status;
  const data = e?.response?.data;
  const code = data?.code;
  if (status === 429 && code === 'AI_RATE_LIMIT_EXCEEDED') {
    const n = Number(data?.retryAfterSec);
    const sec = Number.isFinite(n) && n > 0 ? Math.ceil(n) : 30;
    return tChat('chat.input.err.ai_rate', { sec });
  }
  if (status === 429 && code === 'TOO_MANY_CONNECTIONS') return tChat('chat.input.err.too_many');
  if (status === 429 && code === 'LLM_QUOTA_EXCEEDED') return tChat('chat.input.err.quota');
  if (status === 503) return tChat('chat.input.err.busy');
  if (data?.message) return String(data.message);
  return tChat('chat.input.err.send_failed');
}

export function MessageInput({ roomId }: MessageInputProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  const value = useChatStore((s) => s.inputValueByRoom[roomId] ?? '');
  const setInputValue = useChatStore((s) => s.setInputValue);
  const clearInput = useChatStore((s) => s.clearInput);
  const appendMessage = useChatStore((s) => s.appendMessage);
  const replyingTo = useChatStore((s) => s.replyingToByRoom[roomId]);
  const clearReplyingTo = useChatStore((s) => s.clearReplyingTo);
  const aiModeBase = useChatStore((s) => s.aiModeByRoom[roomId] ?? false);
  const toggleAiMode = useChatStore((s) => s.toggleAiMode);
  // 본인만 멤버인 방 = AI 전용 (NewChatModal 에서 AI 카드로 생성한 방). 토글·banner 숨김.
  const memberCount = useChatStore((s) => s.roomMemberCountByRoom[roomId]);
  const isAiOnlyRoom = memberCount === 1;
  // AI 전용 방은 zustand state(휘발성) 와 무관하게 항상 aiMode=true 강제 — 페이지 새로고침
  // 후 aiModeByRoom 가 reset 되어 /ai prefix 가 누락되는 회귀 차단. ChatArea 의 getRoom()
  // 후 setRoomMemberCount(roomId, 1) 셋되면 즉시 적용.
  const aiMode = isAiOnlyRoom ? true : aiModeBase;
  const setAiPending = useChatStore((s) => s.setAiPending);
  // AI 답변 진행 중이면 입력 잠금(전송·타이핑 차단) — 응답 도착(appendMessage) 또는 180s 안전망에 해제.
  const aiPending = useChatStore((s) => s.aiPendingByRoom[roomId] ?? false);
  // 대시보드 퀵액션 prefill — 일회성 텍스트를 활성 방 입력창에 주입 후 clear.
  // ChatPage 의 catch-up/activeChat effect 와 독립 → 채팅 로직 회귀 격리.
  const pendingComposeText = useChatStore((s) => s.pendingComposeText);
  const setPendingComposeText = useChatStore((s) => s.setPendingComposeText);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 첨부는 내 PC 와 워크스페이스 두 곳에서 고른다(2026-08-26 요청).
  // 파일 고르기 화면은 메일·결재가 쓰는 것을 그대로 쓴다 — 새로 만들지 않는다.
  const myUserId = useAuthStore((s) => (s.user ? String(s.user.id) : null));
  const attachMenu = useAnchoredMenu<HTMLButtonElement>({ width: 232, height: 116 });
  const [wsPickerOpen, setWsPickerOpen] = useState(false);

  useEffect(() => {
    if (!pendingComposeText) return;
    setInputValue(roomId, pendingComposeText);
    setPendingComposeText(null);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [pendingComposeText, roomId, setInputValue, setPendingComposeText]);

  // 대화 진입/전환 시 기본 포커스를 입력창에 둠 — 방을 열면 바로 타이핑 가능(요청 2026-07-01).
  // 단, 검색창·모달 등 다른 입력 요소에 이미 포커스가 있으면 뺏지 않는다(오작동 방지).
  // rAF 로 레이아웃/전환 완료 후 focus. aiPending 이면 textarea disabled 라 자연히 skip.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae && ae !== ta) {
        const tag = ae.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || ae.isContentEditable) return;
      }
      ta.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [roomId]);

  // textarea auto-resize — value 변화 시 scrollHeight 기반으로 높이 동기화.
  // 사용자가 줄바꿈/긴 텍스트 입력 시 textarea 가 자연스럽게 늘어남.
  // max cap 120px 은 CSS `.chat-input-textarea max-height` 와 동일 (이중 안전망).
  // flex column 의 ChatArea 안에서 본 컴포넌트가 늘어나면 MessageList 영역이 자동 축소 →
  // 마지막 메시지 가림 방지는 MessageList 의 ResizeObserver 가 isNearBottom 일 때 scroll bottom 유지.
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [value]);

  // textarea 커서/선택 영역 기준 텍스트 삽입 — 이모지 피커가 사용.
  const insertText = useCallback((text: string) => {
    const ta = textareaRef.current;
    const start = ta?.selectionStart ?? value.length;
    const end = ta?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + text + value.slice(end);
    setInputValue(roomId, next);
    requestAnimationFrame(() => {
      ta?.focus();
      const pos = start + text.length;
      ta?.setSelectionRange(pos, pos);
    });
  }, [roomId, setInputValue, value]);

  // 서식 toolbar(Bold/Italic/List/Code/Link)는 미사용으로 제거(2026-06-30 사용자 요청) —
  // applyWrap/applyLinePrefix 헬퍼도 함께 제거. 이모지 삽입용 insertText 는 유지.

  // attachment upload 는 useAttachmentUpload hook 으로 추출 (cross-domain 공유 — mail/board 도 동일).
  // pattern-shared-component-extraction-cross-domain-7-atomic 의 *2번째 정착 사례*.
  const uploadState = useAttachmentUpload();
  const { attachments, uploadingFile, clearAttachments, addFiles } = uploadState;

  /* ── 붙여넣기·드래그로 첨부 (2026-09-10 사용자 요청) ────────────────────────
   * 여태 첨부는 **클립 단추 하나**로만 올라갔다(드래그&드롭도 없었다).
   * 화면을 캡처해 바로 붙여넣는 것이 가장 잦은 길인데 그게 막혀 있었다.
   */
  const [dragOver, setDragOver] = useState(false);
  /* 안쪽 자식으로 들어갈 때마다 dragleave 가 뜬다 — 셈으로 눌러야 테두리가 깜빡이지 않는다. */
  const dragDepth = useRef(0);

  const onPasteFiles = useCallback((e: ClipboardEvent<HTMLDivElement>) => {
    const files = filesFromTransfer(e.clipboardData);
    if (files.length === 0) return;
    /* ★ **막지 않는다** — 글과 그림이 함께 실려 오면 글은 브라우저가 넣게 둔다. */
    void addFiles(files);
  }, [addFiles]);

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!transferHasFiles(e.dataTransfer)) return;
    /* ★ 여기서 막지 않으면 drop 이 아예 안 온다(브라우저 규약). */
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!transferHasFiles(e.dataTransfer)) return;
    dragDepth.current += 1;
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback(() => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  }, []);

  const onDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    const files = filesFromTransfer(e.dataTransfer);
    dragDepth.current = 0;
    setDragOver(false);
    if (files.length === 0) return;
    /* ★ 드롭은 **반드시 막는다** — 안 막으면 브라우저가 그 파일로 화면을 옮겨 버린다. */
    e.preventDefault();
    void addFiles(files);
  }, [addFiles]);

  const canSend = (value.trim().length > 0 || attachments.length > 0) && !sending && !uploadingFile && !aiPending;

  // 액션 후 textarea 로 focus 복귀 — AI 토글·전송 후 사용자가 바로 다음 입력 가능.
  // requestAnimationFrame 으로 layout 완료 후 focus (textarea height 변동·banner 등 영향 회피).
  const refocusTextarea = useCallback(() => {
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  }, []);

  const handleSend = useCallback(async () => {
    const content = value.trim();
    if ((content.length === 0 && attachments.length === 0) || sending || aiPending) return;
    setSending(true);
    setError(null);
    const idempotencyKey = generateUuid();
    // AI 모드 토글 활성 시 — '/ai ' prefix 자동 부착 (이미 시작하면 skip, 첨부만일 때도 skip)
    const sendContent = (() => {
      if (!content) return ' ';
      if (!aiMode) return content;
      if (/^\/ai(\s|$)/.test(content)) return content;
      return `/ai ${content}`;
    })();
    try {
      /**
       * ★ 채팅은 **언제나 «원문 그대로»** 다 (2026-09-10 사용자 지시).
       *
       * 종전에는 붙여넣기마다 형식을 물었고(팝업·안내 줄) 고른 것만 plain 으로 갔다.
       * 바깥에서 복사하면 브라우저가 거의 항상 `text/html` 을 함께 실어서 «애매할 때만»이
       * 사실상 «거의 언제나»가 됐다 — 물음이 흐름을 끊었다. 그래서 묻는 것을 통째로 걷어내고
       * 기본값을 원문 그대로로 정했다. 적은 대로 보이고, 적은 대로 간다.
       *
       * ⚠ **저장된 대화는 안 건드린다** — 과거 메시지·AI 답변은 `text` 라 종전대로
       *   마크다운으로 그려진다(`MessageItem` 이 contentType 으로 가른다). 바뀌는 것은
       *   **이제부터 내가 보내는 메시지**뿐이다.
       */
      const contentType = 'plain';
      const body = {
        content: sendContent,
        contentType,
        clientMsgId: idempotencyKey,
        attachments: attachments.length > 0 ? attachments.map(toMessageAttachment) : undefined,
        replyToId: replyingTo?.id,
      } as const;

      let res;
      try {
        res = await sendMessage(roomId, body, idempotencyKey);
      } catch (err: unknown) {
        // 아직 plain 을 모르는 서버는 400 을 준다. 형식만 내려놓고 한 번 더 보낸다 —
        // 글을 잃지 않고, 프론트가 서버보다 먼저 나가도 되게 만든다(배포 순서 결합 제거).
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (contentType !== 'plain' || status !== 400) throw err;
        // 본문이 달라지면 서버가 body hash 로 412 를 주므로 키를 새로 뽑는다.
        const retryKey = generateUuid();
        res = await sendMessage(
          roomId,
          { ...body, contentType: 'text', clientMsgId: retryKey },
          retryKey,
        );
      }
      appendMessage(res.message);
      clearInput(roomId);
      clearAttachments();
      clearReplyingTo(roomId);
      // /ai prefix 였으면 AI 응답 대기 — thinking 카드 표시 (AI 응답 도착 시 appendMessage 가 자동 hide).
      // 안전망: 180s 후에도 응답 없으면 자동 hide (gateway-ai timeout 과 일치).
      if (sendContent.startsWith('/ai ')) {
        setAiPending(roomId, true);
        window.setTimeout(() => { setAiPending(roomId, false); }, 180_000);
      }
    } catch (err: unknown) {
      setError(friendlySendError(err));
    } finally {
      setSending(false);
      // 전송 성공/실패 무관 — 사용자가 바로 다음 입력 가능하도록 textarea focus 복원.
      refocusTextarea();
    }
  }, [aiMode, aiPending, appendMessage, attachments, clearAttachments, clearInput, clearReplyingTo, refocusTextarea, replyingTo, roomId, sending, setAiPending, value]);

  /**
   * Enter 로 보내기 — **한글 조합 중에도 한 번에**.
   *
   * 조합을 확정하는 Enter 는 keydown 에서 `isComposing` 이 참이라 종전 조건이 걸러 냈다.
   * 그래서 한글 문장 끝(=늘 조합 중이다)에서는 Enter 를 두 번 눌러야 보내졌다.
   */
  const enterToSend = useEnterToSend<HTMLTextAreaElement>(() => { void handleSend(); });

  return (
    <div className="chat-input">
      <div
        className={`chat-input-shell${dragOver ? ' is-dragover' : ''}`}
        onPaste={onPasteFiles}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* 끌어다 놓는 동안만 — 어디에 놓으면 되는지 보여 준다. */}
        {dragOver && (
          <div className="chat-input-dropzone" role="status">
            <Paperclip size={15} aria-hidden />
            <span>여기에 놓으면 첨부됩니다</span>
          </div>
        )}
        {replyingTo && (
          <div className="chat-input-reply-banner">
            <span className="chat-input-reply-banner-bar" aria-hidden />
            <div className="chat-input-reply-banner-body">
              <span className="chat-input-reply-banner-label">답장</span>
              <span className="chat-input-reply-banner-text">
                {replyingTo.content.replace(/\n/g, ' ').slice(0, 80)}
              </span>
            </div>
            <button
              type="button"
              className="chat-input-reply-banner-cancel"
              onClick={() => clearReplyingTo(roomId)}
              aria-label="답장 취소"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <div className="chat-input-toolbar" role="toolbar" aria-label={tChat('chat.input.aria_toolbar')}>
          {/* 좌측 서식 버튼(Bold/Italic/List/Code/Link)은 미사용으로 제거(2026-06-30). 우측 액션만 유지. */}
          <div className="chat-input-toolbar-spacer" aria-hidden />
          {/* AI 비서 모드 토글 — 활성 시 send 메시지에 '/ai ' prefix 자동 부착.
           * AI 전용 방 (멤버 본인만) 에서는 항상 AI 라 토글 자체를 숨김. */}
          {!isAiOnlyRoom && (
            <button
              type="button"
              className={`chat-input-toolbar-button${aiMode ? ' chat-input-toolbar-button--ai-active' : ''}`}
              onClick={() => { toggleAiMode(roomId); refocusTextarea(); }}
              aria-pressed={aiMode}
              aria-label={tChat(aiMode ? 'chat.input.aiMode.btnOff' : 'chat.input.aiMode.btnOn')}
              title={tChat(aiMode ? 'chat.input.aiMode.btnOff' : 'chat.input.aiMode.btnOn')}
            >
              <Bot size={14} />
            </button>
          )}
          {/* 파일 첨부 — 내 PC 와 워크스페이스 중에서 고른다. */}
          <div className="chat-input-toolbar-popover-wrap">
            <button
              ref={attachMenu.anchorRef}
              type="button"
              className="chat-input-toolbar-button"
              onClick={attachMenu.toggle}
              disabled={!!uploadingFile}
              aria-label={tChat('chat.input.aria_attach_file')}
              aria-haspopup="menu"
              aria-expanded={attachMenu.open}
            >
              <Paperclip size={14} />
            </button>
            {/* 메뉴는 body 로 띄운다 — 입력창 둘레의 상자에 잘리지 않게. */}
            {attachMenu.pos && createPortal(
              <>
                {/* 바깥을 누르면 닫힌다 — 메뉴보다 뒤에 깔아 클릭을 먼저 받는다. */}
                <div className="chat-attachment-dl-veil" role="presentation" onMouseDown={attachMenu.close} />
                <div
                  className="chat-input-attach-menu"
                  role="menu"
                  aria-label="첨부할 파일 고르기"
                  style={{ top: attachMenu.pos.top, left: attachMenu.pos.left }}
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { attachMenu.close(); uploadState.handleAttachClick(); }}
                  >
                    <b>내 PC 에서 선택</b>
                    <span>파일을 올립니다</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { attachMenu.close(); setWsPickerOpen(true); }}
                  >
                    <b>워크스페이스에서 선택</b>
                    <span>이미 올려 둔 파일을 붙입니다</span>
                  </button>
                </div>
              </>,
              document.body,
            )}
          </div>
          {/* 이모지 picker — popover 는 입력 영역 위로 띄움 */}
          <div className="chat-input-toolbar-popover-wrap">
            <button
              type="button"
              className="chat-input-toolbar-button"
              onClick={() => setShowEmoji((v) => !v)}
              aria-label={tChat('chat.input.aria_attach_emoji')}
              aria-expanded={showEmoji}
            >
              <Smile size={14} />
            </button>
            {showEmoji && (
              <EmojiPicker
                onSelect={(emoji) => {
                  insertText(emoji);
                  setShowEmoji(false);
                }}
                onClose={() => setShowEmoji(false)}
              />
            )}
          </div>
          {/* 음성 메시지 — 녹음(MediaRecorder)+업로드 필요, 후속 atomic 까지 비활성 */}
          <button
            type="button"
            className="chat-input-toolbar-button"
            disabled
            aria-label={tChat('chat.input.aria_attach_voice')}
          >
            <Mic size={14} />
          </button>
          {/* @멘션 — 사용자 검색 popover 필요, 후속 atomic 까지 비활성 */}
          <button type="button" className="chat-input-toolbar-button" disabled aria-label="AtSign">
            <AtSign size={14} />
          </button>
        </div>

        <AttachmentUploader state={uploadState} />

        {/* 워크스페이스에 이미 있는 파일을 붙인다 — 다시 올리지 않는다(메일·결재와 같은 화면). */}
        <WorkspaceFilePicker
          open={wsPickerOpen}
          myUserId={myUserId}
          pickedIds={attachments.map((a) => a.fileId)}
          onClose={() => setWsPickerOpen(false)}
          onPick={uploadState.addExisting}
        />

        {aiMode && !isAiOnlyRoom && (
          <div className="chat-input-ai-banner" role="status" aria-live="polite">
            <Bot size={13} aria-hidden />
            <span className="chat-input-ai-banner-text">{tChat('chat.input.aiMode.banner')}</span>
            <span className="chat-input-ai-banner-hint">{tChat('chat.input.aiMode.bannerHint')}</span>
          </div>
        )}
        <div className="chat-input-row">
          <textarea
            ref={textareaRef}
            className="chat-input-textarea"
            value={value}
            onChange={(e) => setInputValue(roomId, e.target.value)}
            // ⚠ 한글 조합 중 Enter 는 keydown 에서 걸러진다(조합을 확정하는 키다) —
            //   그대로 두면 문장 끝에서 Enter 를 두 번 눌러야 한다. 훅이 keyup 에서 갚는다.
            {...enterToSend}
            disabled={aiPending}
            placeholder={aiPending
              ? tChat('chat.input.aiAnswering')
              : aiMode
                ? tChat('chat.input.aiMode.placeholder')
                : tChat('chat.input.placeholder')}
            rows={1}
            // ai-gateway hard cap (DispatchRequestBody.user_text max_length=8000) mirror —
            // pre-validation 으로 422 RTT 회피. soft threshold (4000 char) 는 backend audit-only.
            // 한계: paste 시 브라우저 자동 truncation, 사용자 안내 UI 없음 — 별도 cycle (paste 안내 UX).
            maxLength={8000}
            aria-label={tChat('chat.input.aria_textarea')}
            aria-describedby="chat-input-hint"
          />
          <div className="chat-input-attachments">
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={!canSend}
              data-active={canSend}
              className="chat-input-send-button"
              aria-label={tChat('chat.input.aria_send')}
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      </div>
      <div id="chat-input-hint" className="chat-input-hint">
        <Bot size={11} aria-hidden /> {aiPending ? tChat('chat.input.aiAnswering') : tChat('chat.input.hint')}
        {error && <span className="chat-input-error">· {error}</span>}
      </div>

    </div>
  );
}

/**
 * UUID v4 — Idempotency-Key 용. crypto.randomUUID() 가 안전하면 위임, 아니면 fallback.
 *
 * SSR/구형 브라우저 호환을 위해 fallback 보유. crypto.randomUUID 는 secure context (https /
 * localhost) 에서만 동작 — production 은 https 가 강제이므로 fallback 도달 가능성 낮음.
 */
function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}
